// 数独ソルバー関数
export const solveSudoku = (board) => {
  if (!findEmptyCell(board)) return true;
  
  const [row, col] = findEmptyCell(board);
  
  // 1〜9の数字をランダムな順序で試す
  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  shuffleArray(numbers);
  
  for (const num of numbers) {
    if (isValid(board, row, col, num)) {
      board[row][col] = num;
      
      if (solveSudoku(board)) {
        return true;
      }
      
      board[row][col] = 0;
    }
  }
  
  return false;
};

// 空のセルを見つける関数
export const findEmptyCell = (board) => {
  // セルを順番に探索
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (board[row][col] === 0) {
        return [row, col];
      }
    }
  }
  return null;
};

// 数字の配置が有効かチェックする関数
export const isValid = (board, row, col, num) => {
  // 行をチェック
  for (let x = 0; x < 9; x++) {
    if (board[row][x] === num) return false;
  }
  
  // 列をチェック
  for (let y = 0; y < 9; y++) {
    if (board[y][col] === num) return false;
  }
  
  // 3x3ボックスをチェック
  const boxRow = Math.floor(row / 3) * 3;
  const boxCol = Math.floor(col / 3) * 3;
  
  for (let y = boxRow; y < boxRow + 3; y++) {
    for (let x = boxCol; x < boxCol + 3; x++) {
      if (board[y][x] === num) return false;
    }
  }
  
  return true;
};

// 数独盤面をランダム化する関数
export const randomizeSudokuBoard = (board) => {
  const newBoard = board.map(row => [...row]);
  
  // 数字のマッピングをランダム化
  const mapping = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  shuffleArray(mapping);
  
  // マッピングに従って数字を置換
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (newBoard[row][col] !== 0) {
        newBoard[row][col] = mapping[newBoard[row][col] - 1];
      }
    }
  }
  
  return newBoard;
};

// 行を交換する関数（同じブロック内で）
export const swapRows = (board) => {
  const newBoard = board.map(row => [...row]);
  
  // 各ブロック内で行をランダムに交換
  for (let block = 0; block < 3; block++) {
    const rowIndices = [0, 1, 2];
    shuffleArray(rowIndices);
    
    const tempRows = [
      [...newBoard[block * 3 + 0]],
      [...newBoard[block * 3 + 1]],
      [...newBoard[block * 3 + 2]]
    ];
    
    for (let i = 0; i < 3; i++) {
      newBoard[block * 3 + i] = tempRows[rowIndices[i]];
    }
  }
  
  return newBoard;
};

// 列を交換する関数（同じブロック内で）
export const swapColumns = (board) => {
  const newBoard = Array(9).fill().map(() => Array(9).fill(0));
  
  // 各ブロック内で列をランダムに交換
  for (let block = 0; block < 3; block++) {
    const colIndices = [0, 1, 2];
    shuffleArray(colIndices);
    
    for (let row = 0; row < 9; row++) {
      for (let i = 0; i < 3; i++) {
        newBoard[row][block * 3 + i] = board[row][block * 3 + colIndices[i]];
      }
    }
  }
  
  return newBoard;
};

// 完成した数独盤面を生成する関数
export const generateCompletedSudoku = () => {
  const board = Array(9).fill().map(() => Array(9).fill(0));
  
  // 基本的な解を生成
  solveSudoku(board);
  
  // ランダム変換を適用してバリエーションを作成
  let randomizedBoard = randomizeSudokuBoard(board);
  
  // 行と列の交換をランダムに適用
  randomizedBoard = swapRows(randomizedBoard);
  randomizedBoard = swapColumns(randomizedBoard);
  
  return randomizedBoard;
};

// 配列をシャッフルする関数（Fisher-Yates アルゴリズム）
export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// 解の一意性をチェックする関数
export const hasUniqueSolution = (board) => {
  // ボードのコピーを作成
  const boardCopy = board.map(row => [...row]);
  let solutionCount = 0;
  let solutions = [];
  
  const countSolutions = (board) => {
    if (solutionCount > 1) return;
    
    const emptyCell = findEmptyCell(board);
    if (!emptyCell) {
      solutionCount++;
      // 最初の2つの解のみを保存
      if (solutions.length < 2) {
        solutions.push(board.map(row => [...row]));
      }
      return;
    }
    
    const [row, col] = emptyCell;
    
    // 1から9までの数字を順番に試す
    for (let num = 1; num <= 9; num++) {
      if (isValid(board, row, col, num)) {
        board[row][col] = num;
        countSolutions(board);
        board[row][col] = 0;
      }
    }
  };
  
  countSolutions(boardCopy);
  return { isUnique: solutionCount === 1, solutions };
};

// 最適解を探索する関数
export const findOptimalSolution = (startBoard) => {
  const boardCopy = startBoard.map(row => [...row]);
  const cellsToTry = [];
  
  // 消去可能なセルを特定
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (boardCopy[row][col] !== 0) {
        cellsToTry.push({ row, col, value: boardCopy[row][col] });
      }
    }
  }
  
  const removedCells = [];
  let canRemoveMore = true;
  
  // 最適解を探索
  while (canRemoveMore && cellsToTry.length > 0) {
    canRemoveMore = false;
    
    // ランダムにセルを試す（戦略的なバリエーションのため）
    shuffleArray(cellsToTry);
    
    // すべてのセルをチェック
    for (let i = 0; i < cellsToTry.length; i++) {
      const cell = cellsToTry[i];
      
      // セルの値を一時的に消去
      const originalValue = boardCopy[cell.row][cell.col];
      boardCopy[cell.row][cell.col] = 0;
      
      // 一意解チェック
      const result = hasUniqueSolution(boardCopy);
      
      if (result.isUnique) {
        // このセルは安全に消せる
        removedCells.push({ ...cell, originalValue });
        cellsToTry.splice(i, 1);
        canRemoveMore = true;
        break;
      } else {
        // このセルは消せないので元に戻す
        boardCopy[cell.row][cell.col] = originalValue;
      }
    }
  }
  
  return { 
    finalBoard: boardCopy, 
    removedCells,
    remainingCellsCount: cellsToTry.length
  };
};

// 「あと1マスだけ抜ける状態」の盤面を生成する関数
export const findLastRemovableCellBoard = (originalBoard, recursionCount = 0, maxRecursion = 3) => {
  // 再帰回数が上限を超えたら、シンプルな方法で代替策を提供
  if (recursionCount >= maxRecursion) {
    // シンプルな方法: 最適解を探索し、ランダムに1マスだけ戻す
    const { finalBoard, removedCells } = findOptimalSolution(originalBoard);
    
    // 消せるマスが十分にある場合は、その中からランダムに1つ選んでその状態を返す
    if (removedCells.length > 0) {
      const randomIndex = Math.floor(Math.random() * removedCells.length);
      const cellToKeep = removedCells[randomIndex];
      
      // 消せるマスをランダムに1つ戻す（元の値に復元）
      const alternateBoard = finalBoard.map(row => [...row]);
      alternateBoard[cellToKeep.row][cellToKeep.col] = originalBoard[cellToKeep.row][cellToKeep.col];
      
      // 消せるマスを計算
      const safeRemovableCells = [];
      for (let row = 0; row < 9; row++) {
        for (let col = 0; col < 9; col++) {
          if (alternateBoard[row][col] !== 0) {
            // このマスが消せるかチェック
            const boardCopy = alternateBoard.map(r => [...r]);
            const cellValue = boardCopy[row][col];
            boardCopy[row][col] = 0;
            
            // 一意解チェック
            if (hasUniqueSolution(boardCopy).isUnique) {
              safeRemovableCells.push({ 
                row, 
                col, 
                value: cellValue,
                originalValue: originalBoard[row][col] 
              });
            }
          }
        }
      }
      
      return { 
        board: alternateBoard, 
        removableCells: safeRemovableCells 
      };
    }
    
    // それでも消せるマスがなければ、最初からやり直し - より少ないマスを消す
    const simpleBoardCopy = originalBoard.map(row => [...row]);
    const allCells = [];
    
    // 全セルの位置をリストアップ
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        allCells.push({ row, col });
      }
    }
    
    // ランダムに選んだ20マスのみを消去
    shuffleArray(allCells);
    const cellsToRemove = allCells.slice(0, 20);
    
    for (const cell of cellsToRemove) {
      const { row, col } = cell;
      if (simpleBoardCopy[row][col] !== 0) {
        const temp = simpleBoardCopy[row][col];
        simpleBoardCopy[row][col] = 0;
        
        // 一意解が保たれなければ元に戻す
        if (!hasUniqueSolution(simpleBoardCopy).isUnique) {
          simpleBoardCopy[row][col] = temp;
        }
      }
    }
    
    // 最終的に消せるマスを計算
    const finalRemovableCells = findAllRemovableCells(simpleBoardCopy, originalBoard);
    
    return { 
      board: simpleBoardCopy, 
      removableCells: finalRemovableCells 
    };
  }
  
  // 盤面のコピーを作成
  let boardCopy = originalBoard.map(row => [...row]);
  const allCells = [];
  
  // 全セルの位置をリストアップ
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      allCells.push({ row, col, value: boardCopy[row][col] });
    }
  }
  
  // ランダム順で処理
  shuffleArray(allCells);
  
  // 最初に一定数のマスを消去（効率化のため）
  const initialCellsToRemove = allCells.slice(0, 30); // 40を30に減らす（処理を軽くするため）
  for (const cell of initialCellsToRemove) {
    if (boardCopy[cell.row][cell.col] !== 0) {
      const temp = boardCopy[cell.row][cell.col];
      boardCopy[cell.row][cell.col] = 0;
      
      // 一意解が保たれなければ元に戻す
      if (!hasUniqueSolution(boardCopy).isUnique) {
        boardCopy[cell.row][cell.col] = temp;
      }
    }
  }
  
  // 一意解を保ちながらできるだけマスを消していく（最大試行回数を制限）
  let canRemoveMore = true;
  let tryCount = 0;
  const maxTries = 20; // 最大試行回数を制限
  
  while (canRemoveMore && tryCount < maxTries) {
    canRemoveMore = false;
    tryCount++;
    
    // 全てのマスをシャッフルして順番に試す
    const remainingCells = [];
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (boardCopy[row][col] !== 0) {
          remainingCells.push({ row, col });
        }
      }
    }
    
    shuffleArray(remainingCells);
    
    // 各マスを試す
    for (const cell of remainingCells) {
      const { row, col } = cell;
      const temp = boardCopy[row][col];
      boardCopy[row][col] = 0;
      
      // 一意解チェック
      if (hasUniqueSolution(boardCopy).isUnique) {
        // この数字は消せる
        canRemoveMore = true;
        break;
      } else {
        // この数字は消せないので元に戻す
        boardCopy[row][col] = temp;
      }
    }
  }
  
  // ここで、これ以上マスを消せない状態になっている
  
  // 安全に消せるマスを1つ以上見つける
  // （最終状態からさらに1マス消せる状態にするため）
  const removableCells = findAllRemovableCells(boardCopy, originalBoard);
  
  // 消せるマスが見つからなければ、少し戻して再試行
  if (removableCells.length === 0) {
    // ランダムに数マスを復元して、別の状態を試す
    const filledCells = [];
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (boardCopy[row][col] === 0) {
          filledCells.push({ row, col });
        }
      }
    }
    
    if (filledCells.length > 0) {
      shuffleArray(filledCells);
      const cellsToRestore = filledCells.slice(0, 3); // 5マスを3マスに減らす
      
      for (const cell of cellsToRestore) {
        boardCopy[cell.row][cell.col] = originalBoard[cell.row][cell.col];
      }
      
      // 再試行（再帰回数をカウントアップ）
      return findLastRemovableCellBoard(originalBoard, recursionCount + 1, maxRecursion);
    }
  }
  
  // 消せるマスが見つかった場合
  if (removableCells.length > 0) {
    // 消せるマスをランダムに1つ選んで戻す（元の値に復元）
    const randomIndex = Math.floor(Math.random() * removableCells.length);
    const cellToRestore = removableCells[randomIndex];
    boardCopy[cellToRestore.row][cellToRestore.col] = originalBoard[cellToRestore.row][cellToRestore.col];
    
    // 最終的に消せるマスを再計算
    const finalRemovableCells = findAllRemovableCells(boardCopy, originalBoard);
    
    return { 
      board: boardCopy, 
      removableCells: finalRemovableCells 
    };
  } else {
    // バックアップ処理：それでも消せるマスが見つからない場合
    // もう少し簡単な盤面を生成する
    return findLastRemovableCellBoard(originalBoard, recursionCount + 1, maxRecursion);
  }
};

// 安全に消せるマスを全て見つける関数
export const findAllRemovableCells = (board, originalBoard) => {
  const removableCells = [];
  
  // 全てのマスをチェック
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (board[row][col] !== 0) {
        // このマスが消せるかチェック
        const boardCopy = board.map(r => [...r]);
        const cellValue = boardCopy[row][col];
        boardCopy[row][col] = 0;
        
        // 一意解チェック
        if (hasUniqueSolution(boardCopy).isUnique) {
          removableCells.push({ 
            row, 
            col, 
            value: cellValue,
            originalValue: originalBoard ? originalBoard[row][col] : cellValue
          });
        }
      }
    }
  }
  
  return removableCells;
};

// 指定マスを消すと一意解が失われる理由を説明する関数
export const explainWhyNotRemovable = (board, row, col) => {
  // マスの値を保存
  const cellValue = board[row][col];
  
  // マスを消した盤面をコピー
  const boardCopy = board.map(r => [...r]);
  boardCopy[row][col] = 0;
  
  // 複数解のチェック
  const result = hasUniqueSolution(boardCopy);
  
  if (result.isUnique) {
    return "このマスは実際には安全に消せます。";
  }
  
  // 複数解の分析
  const solutions = result.solutions;
  
  if (solutions.length >= 2) {
    // 影響を受けるマスを特定
    const conflictingCells = [];
    
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (solutions[0][r][c] !== solutions[1][r][c]) {
          conflictingCells.push({ 
            row: r, 
            col: c, 
            values: [solutions[0][r][c], solutions[1][r][c]] 
          });
        }
      }
    }
    
    if (conflictingCells.length > 0) {
      const cell = conflictingCells[0];
      return `行${row + 1}、列${col + 1}の「${cellValue}」を消すと、
行${cell.row + 1}、列${cell.col + 1}のマスに「${cell.values[0]}」または「${cell.values[1]}」が入る可能性ができ、解が一意に定まらなくなります。`;
    }
  }
  
  return "このマスを消すと複数の解が可能になり、一意解が失われます。";
};

// 数独の正当性をチェックする関数
export const checkSudokuValidity = (board) => {
  // 全てのマスが埋まっているかチェック
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (board[row][col] === 0) {
        return false; // 空のマスがある
      }
    }
  }
  
  // 行のチェック
  for (let row = 0; row < 9; row++) {
    const rowNums = new Set();
    for (let col = 0; col < 9; col++) {
      const num = board[row][col];
      if (rowNums.has(num)) {
        return false; // 重複がある
      }
      rowNums.add(num);
    }
  }
  
  // 列のチェック
  for (let col = 0; col < 9; col++) {
    const colNums = new Set();
    for (let row = 0; row < 9; row++) {
      const num = board[row][col];
      if (colNums.has(num)) {
        return false; // 重複がある
      }
      colNums.add(num);
    }
  }
  
  // 3x3ボックスのチェック
  for (let boxRow = 0; boxRow < 3; boxRow++) {
    for (let boxCol = 0; boxCol < 3; boxCol++) {
      const boxNums = new Set();
      for (let r = boxRow * 3; r < boxRow * 3 + 3; r++) {
        for (let c = boxCol * 3; c < boxCol * 3 + 3; c++) {
          const num = board[r][c];
          if (boxNums.has(num)) {
            return false; // 重複がある
          }
          boxNums.add(num);
        }
      }
    }
  }
  
  return true; // すべてのチェックをパス
};

// 全てのセルに対して候補数字を計算する関数
export const calculateCandidates = (board) => {
  // 9x9マス、各マスに1-9の候補数字を保持する配列
  const candidates = Array(9).fill().map(() => 
    Array(9).fill().map(() => new Set([1, 2, 3, 4, 5, 6, 7, 8, 9]))
  );
  
  // すでに値が入っているマスについては、その値のみが候補
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (board[row][col] !== 0) {
        candidates[row][col] = new Set([board[row][col]]);
      }
    }
  }
  
  // 各マスの候補を絞り込む
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (board[row][col] !== 0) {
        const value = board[row][col];
        
        // 同じ行の候補から値を除外
        for (let c = 0; c < 9; c++) {
          if (c !== col && candidates[row][c].has(value)) {
            candidates[row][c].delete(value);
          }
        }
        
        // 同じ列の候補から値を除外
        for (let r = 0; r < 9; r++) {
          if (r !== row && candidates[r][col].has(value)) {
            candidates[r][col].delete(value);
          }
        }
        
        // 同じ3x3ボックスの候補から値を除外
        const boxRow = Math.floor(row / 3) * 3;
        const boxCol = Math.floor(col / 3) * 3;
        
        for (let r = boxRow; r < boxRow + 3; r++) {
          for (let c = boxCol; c < boxCol + 3; c++) {
            if ((r !== row || c !== col) && candidates[r][c].has(value)) {
              candidates[r][c].delete(value);
            }
          }
        }
      }
    }
  }
  
  return candidates;
};

// 単一候補セルを見つける関数
export const findSingleCandidateCells = (board) => {
  const candidates = calculateCandidates(board);
  const singleCandidateCells = [];
  
  // 候補が1つだけのセルを見つける
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (board[row][col] === 0 && candidates[row][col].size === 1) {
        // Setから値を取得
        const value = [...candidates[row][col]][0];
        singleCandidateCells.push({ row, col, value });
      }
    }
  }
  
  return singleCandidateCells;
};

// 単一配置セルを見つける関数
export const findSinglePlacementCells = (board) => {
  const candidates = calculateCandidates(board);
  const singlePlacementCells = [];
  
  // 行における単一配置セルを確認
  for (let row = 0; row < 9; row++) {
    const rowCandidates = new Map();
    
    // 各列の候補を集計
    for (let col = 0; col < 9; col++) {
      if (board[row][col] === 0) {
        for (const num of candidates[row][col]) {
          if (!rowCandidates.has(num)) {
            rowCandidates.set(num, []);
          }
          rowCandidates.get(num).push({ row, col });
        }
      }
    }
    
    // 行内でその数字が配置できる場所が1つしかないマスを探す
    for (const [num, cells] of rowCandidates.entries()) {
      if (cells.length === 1) {
        const { row, col } = cells[0];
        singlePlacementCells.push({ row, col, value: num });
      }
    }
  }
  
  // 列における単一配置セルを確認
  for (let col = 0; col < 9; col++) {
    const colCandidates = new Map();
    
    // 各行の候補を集計
    for (let row = 0; row < 9; row++) {
      if (board[row][col] === 0) {
        for (const num of candidates[row][col]) {
          if (!colCandidates.has(num)) {
            colCandidates.set(num, []);
          }
          colCandidates.get(num).push({ row, col });
        }
      }
    }
    
    // 列内でその数字が配置できる場所が1つしかないマスを探す
    for (const [num, cells] of colCandidates.entries()) {
      if (cells.length === 1) {
        const { row, col } = cells[0];
        singlePlacementCells.push({ row, col, value: num });
      }
    }
  }
  
  // 3x3ボックスにおける単一配置セルを確認
  for (let boxRow = 0; boxRow < 3; boxRow++) {
    for (let boxCol = 0; boxCol < 3; boxCol++) {
      const boxCandidates = new Map();
      
      // ボックス内の候補を集計
      for (let r = boxRow * 3; r < boxRow * 3 + 3; r++) {
        for (let c = boxCol * 3; c < boxCol * 3 + 3; c++) {
          if (board[r][c] === 0) {
            for (const num of candidates[r][c]) {
              if (!boxCandidates.has(num)) {
                boxCandidates.set(num, []);
              }
              boxCandidates.get(num).push({ row: r, col: c });
            }
          }
        }
      }
      
      // ボックス内でその数字が配置できる場所が1つしかないマスを探す
      for (const [num, cells] of boxCandidates.entries()) {
        if (cells.length === 1) {
          const { row, col } = cells[0];
          singlePlacementCells.push({ row, col, value: num });
        }
      }
    }
  }
  
  // 重複を除去
  const uniqueCells = new Map();
  for (const cell of singlePlacementCells) {
    const key = `${cell.row},${cell.col},${cell.value}`;
    uniqueCells.set(key, cell);
  }
  
  return Array.from(uniqueCells.values());
};

// ヒントを生成する関数
export const generateHint = (board) => {
  // 単一候補セルチェック
  const singleCandidateCells = findSingleCandidateCells(board);
  if (singleCandidateCells.length > 0) {
    return {
      message: '「単一候補法」: 空白マスで、ある特定の数字が入る候補が1つしかないマスがあります。',
      count: singleCandidateCells.length
    };
  }

  // 単一配置セルチェック
  const singlePlacementCells = findSinglePlacementCells(board);
  if (singlePlacementCells.length > 0) {
    return {
      message: '「単一配置法」: 行、列、ブロック内で、ある数字が配置できる場所が1つしかないマスがあります。',
      count: singlePlacementCells.length
    };
  }

  // 行、列、ボックスの完成度をチェック
  const completionStatus = checkCompletionStatus(board);
  const leastComplete = completionStatus.sort((a, b) => a.completeCount - b.completeCount)[0];

  if (leastComplete) {
    if (leastComplete.type === 'row') {
      return {
        message: `行${leastComplete.index + 1}に注目してみてください。まだ少し数字が必要です。`,
        count: 0
      };
    } else if (leastComplete.type === 'column') {
      return {
        message: `列${leastComplete.index + 1}に注目してみてください。まだ少し数字が必要です。`,
        count: 0
      };
    } else if (leastComplete.type === 'box') {
      const boxRow = Math.floor(leastComplete.index / 3) + 1;
      const boxCol = (leastComplete.index % 3) + 1;
      return {
        message: `${boxRow}行${boxCol}列目の3x3ボックスに注目してみてください。まだ少し数字が必要です。`,
        count: 0
      };
    }
  }

  // 他に良いヒントが見つからない場合
  return {
    message: '新しい角度から盤面を見てみてください。行、列、ブロックの制約を確認してみましょう。',
    count: 0
  };
};

// 各行、列、ボックスの完成度をチェックする関数
const checkCompletionStatus = (board) => {
  const status = [];

  // 行のチェック
  for (let row = 0; row < 9; row++) {
    const filledCount = board[row].filter(cell => cell !== 0).length;
    status.push({
      type: 'row',
      index: row,
      completeCount: filledCount
    });
  }

  // 列のチェック
  for (let col = 0; col < 9; col++) {
    let filledCount = 0;
    for (let row = 0; row < 9; row++) {
      if (board[row][col] !== 0) filledCount++;
    }
    status.push({
      type: 'column',
      index: col,
      completeCount: filledCount
    });
  }

  // 3x3ボックスのチェック
  for (let box = 0; box < 9; box++) {
    const boxRow = Math.floor(box / 3) * 3;
    const boxCol = (box % 3) * 3;
    let filledCount = 0;
    
    for (let r = boxRow; r < boxRow + 3; r++) {
      for (let c = boxCol; c < boxCol + 3; c++) {
        if (board[r][c] !== 0) filledCount++;
      }
    }
    
    status.push({
      type: 'box',
      index: box,
      completeCount: filledCount
    });
  }

  return status;
};